import React, { useEffect, useState } from "react";
import { axiosInstance } from "./axios-instance";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "./Components/ui/button";
import { Form, FormField } from "./Components/ui/form";
import FormFieldItem from "./common/form/FormFieldItem";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "./Components/ui/input";
import { InputPassword } from "./Components/utils/InputPassword";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./Components/ui/card";
import { LogIn, User, Lock, Sparkles } from "lucide-react";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (values) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.post("/login", values);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("role", res.data.user.role);
      localStorage.setItem("userName", res.data.user.name);
      toast.success(t("gloabl.login_success") || "Login successful!");
      navigate("/branches");
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      if (
        error.response &&
        error.response.status === 422 &&
        (error.response.data.errorCode === 2 ||
          error.response.data.errorCode === 1)
      ) {
        toast.error(t("errorMessage.worngLogin"));
      } else if (error.response) {
        toast.error(
          t("errorMessage.login_failed") || "Login failed. Please try again."
        );
      } else {
        toast.error(
          t("errorMessage.login_failed") || "Login failed. Please try again."
        );
      }
    }
  };

  const formSchema = z.object({
    phone: z
      .string({
        required_error: `${t("gloabl.username_or_phone")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t(
          "gloabl.username_or_phone"
        )} must be at least 2 characters.`,
      }),
    password: z
      .string({
        required_error: `${t("gloabl.Password")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(4, {
        message: `${t("gloabl.Password")} must be at least 4 characters.`,
      }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/branches");
    }
  }, [navigate]);

  const onErorr = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-primary/5 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="border-2 shadow-2xl backdrop-blur-sm bg-background/95">
          <CardHeader className="text-center pb-8 pt-8">
            {/* Logo Section */}
            <div className="flex flex-col items-center gap-6 mb-6">
              <div className="flex items-center justify-center">
                <img
                  alt="Logo"
                  src={require("./assets/logo.png")}
                  className="h-20 w-auto object-contain"
                />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold text-foreground mb-2">
                  {t("gloabl.welcome_back") || "Welcome Back"}
                </CardTitle>
                <CardDescription className="text-base">
                  {t("gloabl.login_description") ||
                    "Sign in to your account to continue"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit, onErorr)}
                noValidate
                className="space-y-6"
              >
                {/* Username or Phone Field */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormFieldItem
                      required
                      label={t("gloabl.username_or_phone")}
                      form={form}
                    >
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground flex items-center gap-1">
                          <User className="w-4 h-4" />
                        </div>
                        <Input
                          placeholder={t("gloabl.enter_username_or_phone")}
                          className="pl-12 h-12"
                          {...field}
                        />
                      </div>
                    </FormFieldItem>
                  )}
                />

                {/* Password Field */}
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormFieldItem
                      required
                      label={t("gloabl.Password")}
                      form={form}
                    >
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">
                          <Lock className="w-5 h-5" />
                        </div>
                        <InputPassword
                          name={"password"}
                          control={form.control}
                          {...field}
                          className="pl-10"
                        />
                      </div>
                    </FormFieldItem>
                  )}
                />

                {/* Login Button */}
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Sparkles className="w-5 h-5 animate-spin" />
                      {t("gloabl.processing") || "Processing..."}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      {t("gloabl.Login")}
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>{`© ${new Date().getFullYear()} All rights reserved`}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
